import React, { useState, useMemo, useRef } from 'react';
import {
    Box,
    Typography,
    Paper,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    useTheme,
    Avatar,
    Chip,
} from '@mui/material';
import withPlanAccess from './WithPlanAccess';

// ─── Helpers ─────────────────────────────────────────────────────────────────

const getInitials = (name) => {
    const names = name.split(' ');
    if (names.length === 1) return name[0].toUpperCase();
    return (names[0][0] + names[names.length - 1][0]).toUpperCase();
};

const getAvatarColor = (name) => {
    const colors = [
        '#1976d2',
        '#dc004e',
        '#388e3c',
        '#f57c00',
        '#6d1b7b',
        '#0288d1',
    ];
    let hash = 0;
    for (let i = 0; i < name.length; i++) {
        hash = name.charCodeAt(i) + ((hash << 5) - hash);
    }
    return colors[Math.abs(hash) % colors.length];
};

const isOperator = (speaker) =>
    speaker.toLowerCase() === 'operator';

const isManagement = (speaker) => {
    const mgmtKeywords = ['ceo', 'cfo', 'coo', 'cto', 'president', 'chief', 'director', 'officer'];
    const lower = speaker.toLowerCase();
    return mgmtKeywords.some(k => lower.includes(k));
};

const parseTranscript = (content) => {
    if (!content) return [];
    return content.split('\n').reduce((acc, line) => {
        const colonIndex = line.indexOf(':');
        if (colonIndex > -1 && colonIndex < 50) {
            acc.push({
                speaker: line.slice(0, colonIndex).trim(),
                text: line.slice(colonIndex + 1).trim()
            });
        } else if (acc.length > 0) {
            acc[acc.length - 1].text += ' ' + line.trim();
        }
        return acc;
    }, []);
};

// ─── Speaker Entry ────────────────────────────────────────────────────────────

const SpeakerEntry = ({ part, theme }) => {
    const operator = isOperator(part.speaker);
    const management = isManagement(part.speaker);

    if (operator) {
        return (
            <Box sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
                py: 0.75,
                px: 2,
                opacity: 0.45,
            }}>
                <Box sx={{
                    width: 6,
                    height: 6,
                    borderRadius: '50%',
                    backgroundColor: theme.palette.text.disabled,
                    flexShrink: 0,
                }} />
                <Typography variant="caption" color="text.disabled" sx={{ fontStyle: 'italic' }}>
                    {part.text}
                </Typography>
            </Box>
        );
    }

    return (
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
            <Avatar
                sx={{
                    bgcolor: getAvatarColor(part.speaker),
                    width: 38,
                    height: 38,
                    flexShrink: 0,
                    fontSize: '0.85rem',
                    fontWeight: 700,
                    boxShadow: management
                        ? `0 0 0 2px ${theme.palette.primary.main}`
                        : 'none',
                }}
            >
                {getInitials(part.speaker)}
            </Avatar>
            <Box
                sx={{
                    flex: 1,
                    backgroundColor: management
                        ? theme.palette.mode === 'dark'
                            ? 'rgba(25, 118, 210, 0.08)'
                            : 'rgba(25, 118, 210, 0.04)'
                        : theme.palette.mode === 'dark'
                            ? 'rgba(255, 255, 255, 0.04)'
                            : 'rgba(0, 0, 0, 0.02)',
                    borderRadius: 2,
                    p: 2,
                    border: management
                        ? `1px solid ${theme.palette.primary.main}22`
                        : `1px solid transparent`,
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.75 }}>
                    <Typography
                        variant="subtitle2"
                        sx={{
                            fontWeight: 700,
                            color: management
                                ? theme.palette.primary.main
                                : theme.palette.text.primary,
                        }}
                    >
                        {part.speaker}
                    </Typography>
                    {management && (
                        <Chip
                            label="Management"
                            size="small"
                            sx={{
                                height: 16,
                                fontSize: '0.6rem',
                                fontWeight: 600,
                                backgroundColor: `${theme.palette.primary.main}22`,
                                color: theme.palette.primary.main,
                                '& .MuiChip-label': { px: 0.75 },
                            }}
                        />
                    )}
                </Box>
                <Typography
                    variant="body2"
                    sx={{
                        lineHeight: 1.75,
                        whiteSpace: 'pre-wrap',
                        color: theme.palette.text.secondary,
                    }}
                >
                    {part.text}
                </Typography>
            </Box>
        </Box>
    );
};

// ─── Main Component ───────────────────────────────────────────────────────────

const EarningsTranscript = ({ data }) => {
    const theme = useTheme();
    const stickyRef = useRef(null);

    const sortedData = useMemo(() => {
        if (!data?.length) return [];
        return [...data].sort((a, b) => {
            if (b.year !== a.year) return b.year - a.year;
            return b.quarter - a.quarter;
        });
    }, [data]);

    const [selectedYear, setSelectedYear] = useState(sortedData[0]?.year || '');
    const [selectedQuarter, setSelectedQuarter] = useState(sortedData[0]?.quarter || '');

    const years = useMemo(() => {
        if (!data?.length) return [];
        return [...new Set(data.map(t => t.year))].sort((a, b) => b - a);
    }, [data]);

    const quarters = useMemo(() => {
        if (!data?.length) return [];
        return [...new Set(data
            .filter(t => t.year === selectedYear)
            .map(t => t.quarter))]
            .sort((a, b) => b - a);
    }, [data, selectedYear]);

    const selectedTranscript = useMemo(() => {
        if (!sortedData.length) return null;
        return sortedData.find(t => t.year === selectedYear && t.quarter === selectedQuarter);
    }, [sortedData, selectedYear, selectedQuarter]);

    if (!sortedData.length) return null;

    const handleYearChange = (event) => {
        const newYear = event.target.value;
        setSelectedYear(newYear);
        const availableQuarters = data
            .filter(t => t.year === newYear)
            .map(t => t.quarter)
            .sort((a, b) => b - a);
        setSelectedQuarter(availableQuarters[0]);
    };

    const handleQuarterChange = (event) => {
        setSelectedQuarter(event.target.value);
    };

    const parsedContent = selectedTranscript
        ? parseTranscript(selectedTranscript.content)
        : [];

    return (
        <Box sx={{ width: '100%', maxWidth: '1200px', mx: 'auto' }}>

            {/* Sticky Header */}
            <Box
                ref={stickyRef}
                sx={{
                    position: 'sticky',
                    top: 0,
                    zIndex: 10,
                    backgroundColor: theme.palette.background.paper,
                    borderBottom: `1px solid ${theme.palette.divider}`,
                    mb: 3,
                    py: 2,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    flexWrap: 'wrap',
                    gap: 2,
                }}
            >
                <Box>
                    <Typography
                        variant="h5"
                        fontWeight={700}
                        sx={{ color: theme.palette.primary.main, lineHeight: 1.2 }}
                    >
                        {selectedTranscript
                            ? `Q${selectedTranscript.quarter} ${selectedTranscript.year} Earnings Call`
                            : 'Earnings Call'}
                    </Typography>
                    {selectedTranscript && (
                        <Typography variant="caption" color="text.secondary" sx={{ mt: 0.25, display: 'block' }}>
                            {new Date(selectedTranscript.date).toLocaleDateString('en-US', {
                                year: 'numeric', month: 'long', day: 'numeric'
                            })}
                        </Typography>
                    )}
                </Box>

                {data.length > 1 && (
                    <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
                        <FormControl size="small" sx={{ minWidth: 90 }}>
                            <InputLabel>Year</InputLabel>
                            <Select value={selectedYear} label="Year" onChange={handleYearChange}>
                                {years.map(year => (
                                    <MenuItem key={year} value={year}>{year}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        <FormControl size="small" sx={{ minWidth: 90 }}>
                            <InputLabel>Quarter</InputLabel>
                            <Select value={selectedQuarter} label="Quarter" onChange={handleQuarterChange}>
                                {quarters.map(quarter => (
                                    <MenuItem key={quarter} value={quarter}>{`Q${quarter}`}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Box>
                )}
            </Box>

            {/* Transcript Content */}
            {selectedTranscript && (
                <Paper
                    elevation={0}
                    sx={{
                        p: { xs: 2, sm: 4 },
                        backgroundColor: theme.palette.mode === 'dark'
                            ? 'rgba(255, 255, 255, 0.02)'
                            : 'rgba(0, 0, 0, 0.01)',
                        borderRadius: 2,
                        border: `1px solid ${theme.palette.divider}`,
                    }}
                >
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                        {parsedContent.map((part, index) => (
                            <SpeakerEntry key={index} part={part} theme={theme} />
                        ))}
                    </Box>
                </Paper>
            )}
        </Box>
    );
};

const allowedPlans = ['Premium'];
export default withPlanAccess(EarningsTranscript, allowedPlans);